import type { TypographyVariantsOptions } from '@mui/material/styles';
import type { CSSProperties } from 'react';

/**
 * Custom Typography variant declarations — tells TypeScript our design system has these
 * variants in addition to MUI's native set. No designer action needed here; this is
 * pure TypeScript plumbing so that `variant="body1Bold"` etc. compile without errors.
 */
declare module '@mui/material/styles' {
  interface TypographyVariants {
    body1Bold: CSSProperties;
    body2Bold: CSSProperties;
    buttonSm: CSSProperties;
    captionBold: CSSProperties;
    captionSm: CSSProperties;
    display: CSSProperties;
    displaySubtitle: CSSProperties;
    overlineBold: CSSProperties;
  }
  interface TypographyVariantsOptions {
    body1Bold?: CSSProperties;
    body2Bold?: CSSProperties;
    buttonSm?: CSSProperties;
    captionBold?: CSSProperties;
    captionSm?: CSSProperties;
    display?: CSSProperties;
    displaySubtitle?: CSSProperties;
    overlineBold?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body1Bold: true;
    body2Bold: true;
    buttonSm: true;
    captionBold: true;
    captionSm: true;
    display: true;
    displaySubtitle: true;
    overlineBold: true;
  }
}

const figtree = '"Figtree", sans-serif';

/**
 * Typography tokens — text styles mirrored from Figma (Design System 2.0 / textStyles).
 * All sizes in px to match Figma pixel values directly.
 *
 * Native MUI variants (h1–h3, body1–2, button, caption, overline) are overridden.
 * h4–h6 are intentionally left at MUI defaults — not used in this design system.
 *
 * Custom variants (display, displaySubtitle, body1Bold, body2Bold, buttonSm, captionBold, captionSm, overlineBold)
 * include an explicit fontFamily because MUI's createTypography.buildVariant() — which
 * auto-injects fontFamily — only runs for the fixed set of native variant names.
 *
 * Figma → MUI variant mapping:
 *   body1             → body1       body1Bold        → body1Bold
 *   body2             → body2       body2Bold        → body2Bold
 *   button            → button      Button Small     → buttonSm
 *   Caption Text      → caption     Caption Text Bold → captionBold
 *   captionSm         → captionSm   (10px small caption — supersedes the old body-xs token)
 *   Overline Text     → overline    Overline Text Bold → overlineBold
 *   Display           → display     (hero/marketing text, larger than h1 — NOT a heading; renders <p>)
 *   Display Subtitle  → displaySubtitle (supporting paragraph under `display`; renders <p>)
 */
export const typography = {
  fontFamily: figtree,
  fontSize: 14,
  htmlFontSize: 16,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 600,

  // — Display ———————————————————————————————————————————————
  // Hero/marketing text (e.g. the SSO login headline). Larger than h1 but not a heading:
  // variantMapping renders it as <p>; pass `component="h1"` where it IS the page heading.
  // Working values (2026-10-12) — pending Figma confirmation.
  display: { fontFamily: figtree, fontWeight: 600, fontSize: '40px', lineHeight: '48px' },
  // Supporting paragraph that accompanies `display` (e.g. the SSO login intro text). Renders <p>.
  displaySubtitle: { fontFamily: figtree, fontWeight: 400, fontSize: '20px', lineHeight: '32px' },

  // — Headings ——————————————————————————————————————————————
  h1: { fontWeight: 700, fontSize: '22px', lineHeight: '36px' },
  h2: { fontWeight: 600, fontSize: '20px', lineHeight: '28px' },
  h3: { fontWeight: 600, fontSize: '16px', lineHeight: '22px' },

  // — Body ——————————————————————————————————————————————————
  body1: { fontWeight: 400, fontSize: '14px', lineHeight: '20px' },
  body1Bold: { fontFamily: figtree, fontWeight: 600, fontSize: '14px', lineHeight: '20px' },
  body2: { fontWeight: 400, fontSize: '12px', lineHeight: '18px' },
  body2Bold: { fontFamily: figtree, fontWeight: 600, fontSize: '12px', lineHeight: '18px' },

  // — Controls ——————————————————————————————————————————————
  button: { fontWeight: 600, fontSize: '14px', lineHeight: '22px', textTransform: 'uppercase' as const },
  buttonSm: {
    fontFamily: figtree,
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '20px',
    textTransform: 'uppercase' as const,
  },

  // — Supplemental ——————————————————————————————————————————
  caption: { fontWeight: 400, fontSize: '12px', lineHeight: '16px' },
  captionBold: { fontFamily: figtree, fontWeight: 600, fontSize: '12px', lineHeight: '16px' },
  captionSm: { fontFamily: figtree, fontWeight: 400, fontSize: '10px', lineHeight: '14px' },
  overline: { fontWeight: 500, fontSize: '12px', lineHeight: '18px', textTransform: 'uppercase' as const },
  overlineBold: {
    fontFamily: figtree,
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '18px',
    textTransform: 'uppercase' as const,
  },
} satisfies TypographyVariantsOptions;

export type TypographyTokens = typeof typography;
